<template>
  <AuthLayout>
    <div class="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:text-center" data-testid="sso-callback">
      <IconLoader2 :size="28" class="tw:animate-spin tw:text-primary" aria-hidden="true" />
      <p class="tw:m-0 tw:text-14 tw:text-gray-600" role="status">{{ $t('sso.callback.signing_in') }}</p>
    </div>
  </AuthLayout>
</template>

<script setup lang="ts">
/** where Google and Microsoft send the browser back to; hands their answer to the backend and moves on */
import { IconLoader2 } from '@tabler/icons-vue';
import { onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { ClientError } from '@/api/client';
import { completeSSO } from '@/api/sso';
import AuthLayout from '@/components/auth/AuthLayout.vue';
import { useSignedInLanding } from '@/composables/useSignedInLanding';
import { isSSOProvider, recallSSOFlow } from '@/composables/useSSOProviders';
import { allowSelfRegistration } from '@/config/app';

const route = useRoute();
const router = useRouter();
const land = useSignedInLanding();

/* --------------------------------- METHODS ---------------------------------- */
function queryString(name: string): string | undefined {
  const value = route.query[name];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** back to the page the sign-in started from, which shows the reason */
function returnWithError(code: string) {
  router.replace({ name: recallSSOFlow() === 'registration' ? 'registration' : 'login', query: { sso_error: code } });
}

async function complete() {
  const provider = route.params.provider;
  const state = queryString('state');

  if (!isSSOProvider(provider) || !state) {
    returnWithError('sso_invalid_state');
    return;
  }

  try {
    const result = await completeSSO(provider, { state, code: queryString('code'), error: queryString('error') });

    if (result.outcome === 'registration_required') {
      // without self-registration the wizard is unreachable, so say why instead of landing on a bare login page
      if (!allowSelfRegistration) {
        returnWithError('sso_no_account');
        return;
      }

      router.replace({ name: 'registration', query: { sso: provider } });
      return;
    }

    // an existing account is only linked once its owner confirms the code sent to its address
    if (result.outcome === 'link_required') {
      // kept in the history state rather than the url, so the address does not end up in logs
      router.replace({
        name: 'sso.link',
        state: {
          provider,
          email: result.email,
          expiresInSeconds: result.expiresInSeconds ?? 0,
          resendAfterSeconds: result.resendAfterSeconds ?? 0,
        },
      });
      return;
    }

    await land(result.returnPath);
  } catch (error) {
    returnWithError((error as ClientError)?.responseJson?.code ?? 'generic');
  }
}

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(complete);
</script>
